import { useEffect, useRef, useState } from 'react'
import { PUBLIC_CHANNELS, maxTextFor, type ChannelType, type ProductDTO, type QuickReplyDTO } from '@sac/contracts'
import { useUi } from '../../state/UiContext'
import { Icon } from '../icons'
import { Menu } from '../ui/Menu'
import { SuggestionCard } from './SuggestionCard'
import { useSuggestionFeedback } from './useSuggestionFeedback'
import { useToast } from '../ui/Toaster'
import { imagesFrom, isImage, prepareImage, type PreparedImage } from '../../lib/images'
import { api, ApiError } from '../../lib/api'
import { useMutation } from '@tanstack/react-query'
import { useAuth } from '../../state/AuthContext'
import { CordialityPopup } from './CordialityPopup'
import { LinkBlockedPopup } from './LinkBlockedPopup'

function autosize(el: HTMLTextAreaElement | null) {
  if (!el) return
  el.style.height = 'auto'
  el.style.height = Math.max(44, Math.min(el.scrollHeight, 180)) + 'px'
}

export function Composer({
  conversationId,
  channelType,
  disabled,
  quickReplies,
  productsFor,
  onSend,
  onSendNote,
}: {
  conversationId: string
  /** Drives the character budget — every marketplace caps one outgoing message. */
  channelType: ChannelType
  disabled?: boolean
  quickReplies: QuickReplyDTO[]
  productsFor: ProductDTO[]
  /** `closeAfter` diz o que o agente escolheu, não o que o canal faz depois. */
  onSend: (
    text: string,
    productIds: string[],
    closeAfter: boolean,
    aiVerbatim?: boolean,
    images?: PreparedImage[],
    cordialityReviewId?: string,
  ) => void
  onSendNote: (text: string) => void
}) {
  const { state, dispatch } = useUi()
  const { user } = useAuth()
  const toast = useToast()
  const taRef = useRef<HTMLTextAreaElement>(null)
  const [qrQuery, setQrQuery] = useState('')

  /**
   * Which side of the reply card is showing.
   *
   * The suggestion and the composer used to be two stacked boxes, which meant the pane always
   * paid for both even though only one of them is ever being used. They are two ways of doing
   * the same thing — answering — so they are two faces of one card.
   *
   * Defaults to the suggestion whenever there is one: it is the cheaper decision to make first.
   */
  /**
   * Imagens anexadas, só nesta tela e só até enviar.
   *
   * Fora do UiContext de propósito: um rascunho de texto sobrevive à troca de conversa porque é
   * barato, mas guardar megabytes de foto por conversa aberta encheria a memória do navegador
   * para algo que ninguém espera reencontrar depois.
   */
  const [images, setImages] = useState<PreparedImage[]>([])
  const [dragging, setDragging] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  useEffect(() => { setImages([]); setDragging(false) }, [conversationId])

  const [face, setFace] = useState<'ai' | 'manual'>('ai')
  const [checking, setChecking] = useState(false)
  const [beforePolish, setBeforePolish] = useState<string | null>(null)
  useEffect(() => { setBeforePolish(null) }, [conversationId])
  const polish = useMutation({
    mutationFn: (text: string) => api.polish(conversationId, text),
    onSuccess: (r, text) => {
      setBeforePolish(text)
      setDraft(r.text)
      taRef.current?.focus()
    },
    onError: (e: Error) => toast(e instanceof ApiError && e.detail ? e.detail : 'Não consegui melhorar o texto agora.'),
  })
  const { send: sendFeedback, hasSuggestion } = useSuggestionFeedback(conversationId)
  useEffect(() => { setFace('ai') }, [conversationId])

  const mode = state.composeMode
  const draft = state.drafts[conversationId] ?? ''
  const attachments = state.attachments[conversationId] ?? []

  /**
   * The channel's cap, and how close this draft is to it.
   *
   * An internal note never leaves the building, so no marketplace limit applies to it.
   *
   * Nothing is truncated and nothing is blocked from being TYPED: the agent keeps the whole
   * text and edits it down. Truncating would publish half a sentence on a public listing, and
   * silently dropping the tail is how someone answers a question they think they answered.
   * What is refused is the SEND — the same refusal the adapter already makes, moved to before
   * the work instead of after it. One reply was genuinely lost this way: 351 characters into a
   * 350-character channel, rejected only once the agent pressed send.
   */
  const limit = mode === 'reply' ? maxTextFor(channelType) : null
  const used = draft.trim().length
  const over = limit !== null && used > limit
  // Quiet until it matters — a counter on every reply is noise on a 4.096-character channel.
  const showCount = limit !== null && used > limit * 0.75
  const sendBlocked =
    Boolean(disabled) || over || checking || (!draft.trim() && attachments.length === 0 && images.length === 0)
  // Uma pergunta pública aceita uma resposta só e se resolve sozinha ao ser respondida.
  const answerClosesThread = PUBLIC_CHANNELS.includes(channelType)

  useEffect(() => {
    autosize(taRef.current)
  }, [draft, mode])

  function setDraft(text: string) {
    dispatch({ type: 'setDraft', conversationId, text })
  }

  const MAX_IMAGENS = 5

  /**
   * O mesmo caminho para as três formas de anexar: botão, arrastar e colar.
   *
   * Uma origem só de verdade — o que muda entre elas é de onde vem o `File`, e nada mais. Três
   * caminhos separados é como um deles acaba com um limite diferente dos outros.
   */
  async function addFiles(files: File[]) {
    const validas = files.filter(isImage)
    if (!validas.length) return
    const espaco = MAX_IMAGENS - images.length
    if (espaco <= 0) return toast(`No máximo ${MAX_IMAGENS} imagens por envio.`)
    if (validas.length > espaco) toast(`Só cabem mais ${espaco}; o resto ficou de fora.`)

    for (const f of validas.slice(0, espaco)) {
      try {
        const pronta = await prepareImage(f)
        setImages((atual) => (atual.length >= MAX_IMAGENS ? atual : [...atual, pronta]))
      } catch (e) {
        toast(e instanceof Error ? e.message : 'Não consegui preparar a imagem.')
      }
    }
  }

  /**
   * Cordialidade: antes de um texto escrito ou editado à mão sair, o servidor avalia o tom. Se
   * soar seco, abre o aviso e o rascunho fica onde está até a pessoa escolher. Qualquer falha
   * (rede, prazo, servidor) envia como estava — o aviso nunca pode impedir uma resposta.
   */
  const [review, setReview] = useState<
    | null
    | { reviewId: string; reason: string; suggestion: string | null; original: string; closeAfter: boolean; via: 'composer' | 'suggestion' }
  >(null)
  useEffect(() => { setReview(null); setChecking(false) }, [conversationId])

  /**
   * Links: a regra do canal (Configurações → Links) vem antes de tudo. Só pergunta ao servidor
   * quando o texto tem cara de link — o servidor confere de novo no envio de qualquer jeito.
   */
  const [linkBlock, setLinkBlock] = useState<null | { domains: string[]; scopeLabel: string; text: string }>(null)
  useEffect(() => { setLinkBlock(null) }, [conversationId])
  async function linksOk(text: string): Promise<boolean> {
    if (!/https?:\/\/|www\.|\b[a-z0-9-]+\.(com|br|net|org|shop|store|io|be|ly|me|co|app|link|site|online|info)\b/i.test(text)) return true
    setChecking(true)
    const r = await api.linkCheck(conversationId, text).catch(() => null)
    setChecking(false)
    if (r?.blocked.length) {
      setLinkBlock({ domains: r.blocked, scopeLabel: r.scopeLabel, text })
      return false
    }
    return true
  }

  /** Devolve o aviso a mostrar, ou o id da avaliação (se houve) para ir junto com o envio. */
  async function cordial(text: string, closeAfter: boolean, via: 'composer' | 'suggestion'): Promise<{ warned: boolean; reviewId?: string }> {
    if (!user?.cordialityCheck || !text) return { warned: false }
    setChecking(true)
    const r = await api.cordialityCheck(conversationId, text).catch(() => null)
    setChecking(false)
    if (r?.status === 'warn') {
      setReview({ reviewId: r.reviewId, reason: r.reason, suggestion: r.suggestion, original: text, closeAfter, via })
      return { warned: true }
    }
    return { warned: false, reviewId: r?.reviewId ?? undefined }
  }

  function sendFromComposer(text: string, closeAfter: boolean, reviewId?: string) {
    // A reply typed from scratch while a suggestion was on the table is the strongest signal
    // the loop can get, and until the composer knew about the suggestion it was recorded
    // nowhere. Sources are left untouched — see feedback.ts on why `rewritten` is neither a
    // promotion nor a demotion. No-ops when nothing was suggested.
    sendFeedback('rewritten', text)
    setBeforePolish(null)
    onSend(text, attachments, closeAfter, undefined, images, reviewId)
    setImages([])
    dispatch({ type: 'clearComposer', conversationId })
  }

  function sendFromSuggestion(text: string, verbatim: boolean, reviewId?: string) {
    onSend(text, attachments, true, verbatim, undefined, reviewId)
    dispatch({ type: 'clearComposer', conversationId })
  }

  async function submit(closeAfter = false) {
    if (checking) return
    if (mode === 'note') {
      if (!draft.trim()) return
      onSendNote(draft.trim())
      setImages([])
      dispatch({ type: 'clearComposer', conversationId })
      return
    }
    if (!draft.trim() && attachments.length === 0 && images.length === 0) return
    if (over) return
    const text = draft.trim()
    if (text && !(await linksOk(text))) return
    const g = await cordial(text, closeAfter, 'composer')
    if (!g.warned) sendFromComposer(text, closeAfter, g.reviewId)
  }

  const filteredQuick = quickReplies.filter(
    (q) => !qrQuery || q.title.toLowerCase().includes(qrQuery.toLowerCase()) || q.body.toLowerCase().includes(qrQuery.toLowerCase()),
  )

  const suggestion =
    mode === 'reply' && !disabled ? (
      <SuggestionCard
        conversationId={conversationId}
        channelType={channelType}
        onFlip={() => setFace('manual')}
        onUse={async (text, verbatim) => {
          // Goes through the normal draft + send path, so an AI-assisted reply is delivered,
          // validated and audited identically to a hand-typed one. Accepting a suggestion is
          // answering the question that was asked, so it takes the same default as the button:
          // close, and let the follow-up prompt handle the case where it should not have.
          setDraft(text)
          // Sugestão intacta não passa pelo aviso: só o que alguém escreveu ou mexeu.
          if (!(await linksOk(text.trim()))) return
          const g = verbatim ? { warned: false } : await cordial(text.trim(), true, 'suggestion')
          if (!g.warned) sendFromSuggestion(text, verbatim, g.reviewId)
        }}
      />
    ) : null

  // An internal note is not an answer to the customer, so no suggestion applies to it — and
  // with nothing suggested there is no second face, only the composer.
  const flippable = hasSuggestion && mode === 'reply' && !disabled
  const showing: 'ai' | 'manual' = flippable ? face : 'manual'

  const composer = (
      <div
        className={`comp ${mode === 'note' ? 'note-mode' : ''} ${dragging ? 'dragging' : ''}`}
        // O alvo é a caixa inteira, não só o campo de texto: mirar um retângulo de 44px de
        // altura com um arquivo na mão é a diferença entre funcionar e "não aceitou".
        onDragOver={(e) => {
          if (disabled || mode === 'note') return
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={(e) => {
          // Só quando sai da caixa de verdade: passar por cima de um filho dispara leave e
          // faria a moldura piscar durante todo o arrasto.
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false)
        }}
        onDrop={(e) => {
          if (disabled || mode === 'note') return
          e.preventDefault()
          setDragging(false)
          void addFiles(imagesFrom(e.dataTransfer))
        }}
      >
        <div className="comp-tabs">
          <button className={`ctab ${mode === 'reply' ? 'on' : ''}`} onClick={() => dispatch({ type: 'setComposeMode', mode: 'reply' })}>
            Resposta
          </button>
          <button className={`ctab ${mode === 'note' ? 'on' : ''}`} onClick={() => dispatch({ type: 'setComposeMode', mode: 'note' })}>
            Nota interna
          </button>
          {flippable && (
            <button className="ctab flip-back" onClick={() => setFace('ai')} title="Voltar para a sugestão da IA">
              <Icon name="spark" size={12} />
              Ver sugestão
            </button>
          )}
        </div>
        {mode === 'note' && (
          <div className="note-warn">
            <Icon name="lock" size={12} />
            Somente a equipe vê esta mensagem. O cliente não recebe.
          </div>
        )}
        {mode === 'reply' && attachments.length > 0 && (
          <div className="chips">
            {attachments.map((pid, i) => {
              const p = productsFor.find((x) => x.id === pid)
              return (
                <span className="chip" key={i}>
                  {p?.imageEmoji ?? '📦'} {p?.name ?? pid}
                  <button className="x" onClick={() => dispatch({ type: 'removeAttachment', conversationId, index: i })}>
                    <Icon name="x" size={11} />
                  </button>
                </span>
              )
            })}
          </div>
        )}
        {mode === 'reply' && images.length > 0 && (
          <div className="img-chips">
            {images.map((img, i) => (
              <span className="img-chip" key={i}>
                <img src={img.dataUrl} alt="" />
                <button
                  className="x"
                  title="Remover"
                  onClick={() => setImages((a) => a.filter((_, j) => j !== i))}
                >
                  <Icon name="x" size={11} />
                </button>
              </span>
            ))}
          </div>
        )}
        <textarea
          ref={taRef}
          rows={1}
          placeholder={mode === 'note' ? 'Escreva uma nota interna…' : 'Digite sua resposta…'}
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onPaste={(e) => {
            if (mode === 'note') return
            const arquivos = imagesFrom(e.clipboardData)
            // Só engole o evento quando havia mesmo imagem: colar texto tem que continuar
            // colando texto.
            if (arquivos.length) {
              e.preventDefault()
              void addFiles(arquivos)
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              // Enter faz o que o botão padrão faz — o da direita, que é "Enviar". Só fecha quando
              // fechar é a única saída que existe (quadro de perguntas); nota interna só é salva.
              submit(mode === 'reply' && answerClosesThread)
            }
          }}
        />
        <div className="comp-bar">
          {/* Um botão só. O "anexar arquivo" genérico foi embora: nenhum dos três canais que
              enviam anexo hoje aceita PDF ou documento pelo SAC, e um botão que sempre falha é
              pior que a ausência dele. */}
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            multiple
            hidden
            onChange={(e) => {
              void addFiles(Array.from(e.target.files ?? []))
              // Zera para que escolher o MESMO arquivo de novo dispare o evento outra vez.
              e.target.value = ''
            }}
          />
          <button
            className="cbtn"
            title="Enviar imagem — ou arraste para cá, ou cole com Ctrl+V"
            disabled={disabled || mode === 'note'}
            onClick={() => fileRef.current?.click()}
          >
            <Icon name="img" />
          </button>
          <button
            className="cbtn"
            title="Emoji"
            onClick={() => setDraft(draft + ['😊', '🙂', '👍', '🙏'][Math.floor(Math.random() * 4)])}
          >
            <Icon name="smile" />
          </button>
          <Menu
            trigger={(open) => (
              <button className="cbtn qr" onClick={open} title="Respostas rápidas" aria-label="Respostas rápidas">
                <Icon name="bolt" />
              </button>
            )}
          >
            {(close) => (
              <div style={{ width: 330 }}>
                <div className="pop-search">
                  <input
                    autoFocus
                    placeholder="Buscar resposta rápida…"
                    value={qrQuery}
                    onChange={(e) => setQrQuery(e.target.value)}
                  />
                </div>
                {filteredQuick.length === 0 ? (
                  <div className="empty" style={{ padding: 18 }}>
                    Nada encontrado.
                  </div>
                ) : (
                  filteredQuick.map((q) => (
                    <button
                      key={q.id}
                      className="qr-item"
                      onClick={() => {
                        setDraft(draft ? draft + '\n\n' + q.body : q.body)
                        close()
                        setQrQuery('')
                      }}
                    >
                      <span className="t">{q.title}</span>
                      <span className="b">{q.body}</span>
                    </button>
                  ))
                )}
                <div className="cfg-note" style={{ padding: '6px 9px 2px' }}>Criar ou editar: Configurações → Respostas rápidas</div>
              </div>
            )}
          </Menu>

          {/* Melhorar com IA: o mesmo do quadro de edição da sugestão — deixa o texto mais cordial e
              claro com a conversa como contexto; preço, prazo, link e código não mudam (ver
              polishReply). Some na nota interna: não é mensagem para cliente. */}
          {mode === 'reply' && (
            <span className="cz-polish">
              {beforePolish !== null && (
                <button className="btn ghost sm" onClick={() => { setDraft(beforePolish); setBeforePolish(null) }}>
                  Desfazer
                </button>
              )}
              <button
                className="btn ghost sm"
                disabled={!draft.trim() || polish.isPending || Boolean(disabled)}
                title="Deixa a resposta mais cordial e clara usando a conversa como contexto. Preços, prazos, links e números não mudam."
                onClick={() => polish.mutate(draft)}
              >
                <Icon name="spark" size={12} /> {polish.isPending ? 'Melhorando…' : 'Melhorar com IA'}
              </button>
            </span>
          )}
          {showCount && (
            <span className={`cz-count ${over ? 'over' : ''}`} title={`Limite do canal: ${limit} caracteres`}>
              {used}/{limit}
            </span>
          )}
          {/* Duas saídas, porque são duas decisões diferentes: "respondi e acabou" e "respondi e
              ainda espero algo". "Enviar" é a primária e fica à direita: fechar é o passo que tira
              a conversa da fila, e por isso é o que pede o clique deliberado, não o reflexo. */}
          {mode === 'note' ? (
            <button className="btn pri sm cz-send" disabled={!draft.trim()} onClick={() => submit()}>
              Salvar nota
            </button>
          ) : answerClosesThread ? (
            // Num quadro de perguntas a resposta encerra a conversa de qualquer jeito — oferecer
            // "Enviar" sem fechar seria oferecer algo que não acontece.
            <button
              className="btn pri sm cz-send"
              disabled={sendBlocked}
              title={over ? `Passou ${used - limit!} caracteres do limite do canal` : undefined}
              onClick={() => submit(true)}
            >
              {checking ? 'Verificando…' : 'Enviar e fechar'}
            </button>
          ) : (
            <>
              <button
                className="btn sec sm cz-send"
                disabled={sendBlocked}
                title={over ? `Passou ${used - limit!} caracteres do limite do canal` : 'Envia e fecha a conversa'}
                onClick={() => submit(true)}
              >
                Enviar e fechar
              </button>
              <button
                className="btn pri sm cz-send"
                disabled={sendBlocked}
                title={over ? `Passou ${used - limit!} caracteres do limite do canal` : 'Envia e mantém a conversa aberta (Enter)'}
                onClick={() => submit(false)}
              >
                Enviar
              </button>
            </>
          )}
        </div>
      </div>
  )

  return (
    <div className="cz-inner">
      {flippable ? (
        <div className={`flip ${showing === 'manual' ? 'turned' : ''}`}>
          <div className="flip-in">
            {/* `inert` on the face turned away: backface-visibility hides it, but without this
                its textarea still answers Tab and steals the keyboard. */}
            <div className="flip-face" inert={showing !== 'ai'}>{suggestion}</div>
            <div className="flip-face back" inert={showing !== 'manual'}>{composer}</div>
          </div>
        </div>
      ) : (
        <>
          {/* Nothing to turn over: the launcher is one small row, so it stays above the box the
              way it always did rather than pretending to be a card with two sides. */}
          {suggestion}
          {composer}
        </>
      )}
      <div className="hint">Enter envia · Shift+Enter quebra linha</div>
      {linkBlock && (
        <LinkBlockedPopup
          conversationId={conversationId}
          domains={linkBlock.domains}
          scopeLabel={linkBlock.scopeLabel}
          text={linkBlock.text}
          onClose={() => {
            setLinkBlock(null)
            setFace('manual')
            taRef.current?.focus()
          }}
        />
      )}
      {review && (
        <CordialityPopup
          reason={review.reason}
          original={review.original}
          suggestion={review.suggestion}
          onSend={(text) => {
            const r = review
            setReview(null)
            if (r.via === 'composer') sendFromComposer(text, r.closeAfter, r.reviewId)
            else sendFromSuggestion(text, false, r.reviewId)
          }}
          onCancel={() => {
            void api.cordialityCancel(review.reviewId).catch(() => {})
            setReview(null)
            taRef.current?.focus()
          }}
        />
      )}
    </div>
  )
}
